import { X } from 'lucide-react';
import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { isTopMostModal, pushModal, removeModal } from './modalStack';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';
export type ModalAlign = 'start' | 'center' | 'sheet';

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
};

const ALIGN_CLASSES: Record<ModalAlign, string> = {
  start: 'items-start',
  center: 'items-center',
  sheet: 'items-end sm:items-center',
};

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  open: boolean;
  /** ESC, backdrop tıklaması ve varsayılan kapat (X) butonu bu callback'i çağırır. */
  onClose: () => void;
  /** Verilirse sticky başlık (başlık + X butonu) render edilir. */
  title?: ReactNode;
  /** Varsayılan başlıkta X butonunun yanına ek içerik. */
  headerActions?: ReactNode;
  /** Varsayılan başlıktaki X butonunu gizler. */
  hideCloseButton?: boolean;
  size?: ModalSize;
  /** start: üstten, center: ortada, sheet: mobilde alt sheet / masaüstünde ortada. */
  align?: ModalAlign;
  /** true ise ESC, backdrop ve X ile kapanma devre dışı kalır. */
  disableClose?: boolean;
  /** İçerikteki başlık öğesinin id'si (aria-labelledby). title prop'u verilmişse gerekmez. */
  labelledBy?: string;
  /** Görünür başlık yoksa erişilebilir ad (aria-label). */
  ariaLabel?: string;
  zIndex?: number;
  /** Overlay arka plan/padding sınıfları (varsayılan: "bg-black/50 p-4"). */
  overlayClassName?: string;
  /** Panel varsayılanlarına eklenen sınıflar (padding vb.). */
  panelClassName?: string;
  /** true ise panel varsayılan sınıfları uygulanmaz; panelClassName tam kontrol sağlar. */
  panelUnstyled?: boolean;
  /** Overlay öğesine uygulanır. */
  'data-testid'?: string;
  /** Panel (dialog) öğesine uygulanır. */
  panelTestId?: string;
  /** Açılışta odaklanılacak öğe seçicisi; verilmezse ilk odaklanabilir öğe. */
  initialFocusSelector?: string;
  children: ReactNode;
}

/**
 * Merkezi, erişilebilir modal bileşeni.
 *
 * - role="dialog" + aria-modal + aria-labelledby/aria-label
 * - Odak tuzağı (Tab / Shift+Tab içeride tutar)
 * - Açılışta ilk odaklanabilir öğeye odak, kapanışta önceki odağa dönüş
 * - ESC ve backdrop tıklaması ile kapanma (disableClose ile kapatılabilir)
 * - İç içe modallarda yalnız en üstteki modal ESC/odak olaylarını işler
 *
 * Migrasyon notu: frontend/src/components/common/MODAL_MIGRATION.md
 */
export function Modal({
  open,
  onClose,
  title,
  headerActions,
  hideCloseButton = false,
  size = 'md',
  align = 'center',
  disableClose = false,
  labelledBy,
  ariaLabel,
  zIndex = 50,
  overlayClassName,
  panelClassName,
  panelUnstyled = false,
  'data-testid': testId,
  panelTestId,
  initialFocusSelector,
  children,
}: ModalProps) {
  const instanceId = useRef(Symbol('modal'));
  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const autoTitleId = useId();

  // onClose referansı her render'da değişebilir (inline arrow); ana effect'in
  // gereksiz yere yeniden kurulup odağı sıfırlamasını önlemek için ref'te tut.
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const titleId = title ? `${autoTitleId}-title` : undefined;
  const resolvedLabelledBy = titleId ?? labelledBy;

  useEffect(() => {
    if (!open) return undefined;

    const id = instanceId.current;
    pushModal(id);
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const isTopMost = () => isTopMostModal(id);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isTopMost()) return;

      if (e.key === 'Escape') {
        if (!disableClose) onCloseRef.current();
        return;
      }

      if (e.key !== 'Tab' || !panelRef.current) return;

      const focusable = panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Açılış odağı: özel seçici → ilk odaklanabilir öğe → panel
    const panel = panelRef.current;
    if (panel) {
      const target = initialFocusSelector
        ? panel.querySelector<HTMLElement>(initialFocusSelector)
        : panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (target ?? panel).focus();
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      removeModal(id);
      const previous = previousFocusRef.current;
      previousFocusRef.current = null;
      if (previous && document.contains(previous)) {
        previous.focus();
      }
    };
  }, [open, disableClose, initialFocusSelector]);

  if (!open) return null;

  const panelClasses = panelUnstyled
    ? (panelClassName ?? '')
    : `w-full ${SIZE_CLASSES[size]} max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-xl${
        panelClassName ? ` ${panelClassName}` : ''
      }`;

  return createPortal(
    <div
      className={`fixed inset-0 flex justify-center ${ALIGN_CLASSES[align]} ${
        overlayClassName ?? 'bg-black/50 p-4'
      }`}
      style={{ zIndex }}
      role="presentation"
      data-testid={testId}
      onClick={(e) => {
        if (e.target === e.currentTarget && !disableClose) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={resolvedLabelledBy}
        aria-label={resolvedLabelledBy ? undefined : ariaLabel}
        tabIndex={-1}
        data-testid={panelTestId}
        className={panelClasses}
        onClick={(e) => e.stopPropagation()}
      >
        {title !== undefined && title !== null && (
          <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-6 py-4">
            <h2 id={titleId} className="text-lg font-semibold text-slate-900">
              {title}
            </h2>
            <div className="flex items-center gap-2">
              {headerActions}
              {!hideCloseButton && (
                <button
                  type="button"
                  aria-label="Kapat"
                  onClick={() => {
                    if (!disableClose) onClose();
                  }}
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
}
